<template>
  <div class="training-page">
    <div class="page-header">
      <div class="nav-bar">
        <div class="nav-back" @click="goBack">
          <van-icon name="arrow-left" size="20" />
        </div>
        <h1 class="nav-title">{{ pageTitle }}</h1>
        <div class="nav-placeholder" />
      </div>
    </div>

    <div class="hub-section" v-if="!examCategoryId && !loading">
      <div class="status-banner" v-if="!hasAccess">
        <span class="status-text">暂未购买本题库</span>
        <span class="status-link" @click="goVip">去开通</span>
      </div>

      <div class="feature-cards" v-if="examCategories.length">
        <div
          v-for="exam in examCategories"
          :key="exam.id"
          class="feature-card"
          :class="'theme-' + examCardMeta(exam).theme"
          @click="openExamCategory(exam)"
        >
          <div class="feature-card-text">
            <h3>{{ exam.name }}</h3>
            <p>{{ examCardMeta(exam).subtitle }}</p>
            <span class="feature-card-count">{{ examSubCount(exam) }}套</span>
          </div>
          <div class="feature-card-icon">
            <van-icon :name="examCardMeta(exam).icon" size="28" color="#fff" />
          </div>
        </div>
      </div>
    </div>

    <div class="tabs-wrap" v-if="!examCategoryId && trainingCategories.length && !loading">
      <div class="tabs-scroll">
        <div
          v-for="cat in trainingCategories"
          :key="cat.id"
          class="tab-item"
          :class="{ active: activeCategoryId === parseInt(cat.id, 10) }"
          @click="selectCategory(cat.id)"
        >
          {{ cat.name }}
        </div>
      </div>
    </div>

    <div class="list-section" v-if="!loading">
      <div v-if="activeSubCategories.length" class="module-list">
        <div
          v-for="sub in activeSubCategories"
          :key="sub.id"
          class="module-card"
          :class="{ locked: !hasAccess, empty: sub.question_count <= 0 }"
          @click="onModuleClick(sub)"
        >
          <div class="module-accent" :class="listAccentClass" />
          <div class="module-body">
            <span class="module-name">{{ sub.name }}</span>
            <span class="module-count">{{ sub.question_count }}题</span>
          </div>
          <van-icon
            v-if="!hasAccess"
            name="lock"
            size="18"
            class="module-lock"
          />
          <van-icon
            v-else-if="sub.question_count > 0"
            name="arrow"
            size="16"
            class="module-arrow"
          />
        </div>
      </div>
      <van-empty v-else :description="emptyDescription" />
    </div>

    <div class="loading-wrap" v-else>
      <van-loading type="spinner" color="#1989fa" />
    </div>

    <div class="bottom-cta" v-if="!hasAccess && bankId > 0">
      <van-button round block type="primary" class="trial-btn" @click="goVip">
        立即试用
      </van-button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { isExamCategory, examCardMeta } from '@/constants/training'

export default {
  name: 'TrainingList',
  data() {
    return {
      bankId: 0,
      bankName: '',
      hasAccess: true,
      categories: [],
      activeCategoryId: 0,
      examCategoryId: 0,
      loading: true
    }
  },
  computed: {
    pageTitle() {
      if (this.examCategoryId > 0) {
        const cat = this.categories.find(c => parseInt(c.id, 10) === this.examCategoryId)
        return cat ? cat.name : this.bankName || '题库训练'
      }
      return this.bankName || '题库训练'
    },
    examCategories() {
      return (this.categories || []).filter(isExamCategory)
    },
    trainingCategories() {
      return (this.categories || []).filter(c => !isExamCategory(c))
    },
    activeCategory() {
      if (this.examCategoryId > 0) {
        return this.categories.find(c => parseInt(c.id, 10) === this.examCategoryId) || null
      }
      return this.trainingCategories.find(c => parseInt(c.id, 10) === this.activeCategoryId) || null
    },
    activeSubCategories() {
      const cat = this.activeCategory
      return cat ? (cat.sub_categories || []) : []
    },
    listAccentClass() {
      if (!this.examCategoryId) return ''
      const cat = this.activeCategory
      if (!cat) return ''
      return cat.kind === 'mock_exam' || cat.name === '模拟考试' ? 'accent-mock' : 'accent-past'
    },
    emptyDescription() {
      if (this.examCategoryId > 0) {
        return '暂无试卷，请在后台「分类管理」中添加子项'
      }
      return '该分类下暂无训练项'
    }
  },
  watch: {
    '$route.query.exam_category_id'() {
      this.syncRouteState()
      if (!this.loading) {
        this.examCategoryId = parseInt(this.$route.query.exam_category_id, 10) || 0
      }
    }
  },
  async mounted() {
    this.bankId = parseInt(this.$route.query.bank_id, 10) || 0
    this.bankName = this.$route.query.bank_name || ''
    if (this.bankId <= 0) {
      Toast.fail('请选择题库')
      this.$router.replace('/home')
      return
    }
    this.syncRouteState()
    await this.loadData()
  },
  methods: {
    examCardMeta,
    examSubCount(exam) {
      return (exam.sub_categories || []).length
    },
    syncRouteState() {
      this.examCategoryId = parseInt(this.$route.query.exam_category_id, 10) || 0
    },
    async loadData() {
      this.loading = true
      try {
        const banksData = await this.$store.dispatch('fetchQuestionBanks')
        const bank = (banksData.banks || []).find(b => b.id === this.bankId)
        if (bank) {
          this.hasAccess = !!bank.has_access
          if (!this.bankName) {
            this.bankName = bank.name
          }
        }
        const categories = await this.$store.dispatch('fetchCategories', this.bankId)
        this.categories = categories || []

        if (this.examCategoryId > 0) {
          const exists = this.categories.some(c => parseInt(c.id, 10) === this.examCategoryId)
          if (!exists) {
            this.examCategoryId = 0
          }
        }

        if (!this.examCategoryId) {
          const tabs = this.trainingCategories
          if (tabs.length) {
            const cur = tabs.find(c => parseInt(c.id, 10) === this.activeCategoryId)
            this.activeCategoryId = cur
              ? parseInt(cur.id, 10)
              : parseInt(tabs[0].id, 10) || 0
          } else {
            this.activeCategoryId = 0
          }
        }
      } catch (err) {
        Toast.fail(err.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openExamCategory(exam) {
      const id = parseInt(exam.id, 10)
      if (!id) return
      this.$router.push({
        path: '/training',
        query: {
          bank_id: String(this.bankId),
          bank_name: this.bankName,
          exam_category_id: String(id)
        }
      }).catch(() => {})
    },
    selectCategory(id) {
      this.activeCategoryId = parseInt(id, 10) || 0
    },
    onModuleClick(sub) {
      if (!this.hasAccess) {
        this.goVip()
        return
      }
      if (!sub.question_count || sub.question_count <= 0) {
        Toast('该训练项暂无题目')
        return
      }
      const cat = this.activeCategory
      this.$router.push({
        path: '/quiz',
        query: {
          bank_id: String(this.bankId),
          bank_name: this.bankName,
          category_id: cat ? String(cat.id) : '',
          category_name: cat ? cat.name : '',
          sub_category_id: String(sub.id),
          sub_category_name: sub.name
        }
      })
    },
    goVip() {
      this.$router.push({
        path: '/vip',
        query: { bank_id: this.bankId, bank_name: this.bankName }
      })
    },
    goBack() {
      if (this.examCategoryId > 0) {
        this.$router.replace({
          path: '/training',
          query: {
            bank_id: String(this.bankId),
            bank_name: this.bankName
          }
        }).catch(() => {})
        return
      }
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.training-page {
  min-height: 100vh;
  background: #f4f6f9;
  padding-bottom: calc(108px + env(safe-area-inset-bottom));
}

.page-header {
  background: #fff;
  padding-top: env(safe-area-inset-top);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
}

.nav-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 12px;
}

.nav-back {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5c4a3a;
}

.nav-title {
  flex: 1;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  color: #3d3229;
  margin: 0;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-placeholder {
  width: 40px;
}

.hub-section {
  padding: 12px 16px 4px;
}

.status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: linear-gradient(90deg, #fff7e6, #fff);
  border-radius: 10px;
  border: 1px solid #ffe7ba;
  font-size: 13px;
  color: #ad6800;
}

.status-link {
  color: #1989fa;
  font-weight: 500;
}

.feature-cards {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.feature-cards::-webkit-scrollbar {
  display: none;
}

.feature-card {
  flex: 0 0 calc(50% - 6px);
  min-width: calc(50% - 6px);
  max-width: calc(50% - 6px);
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 14px 12px;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.feature-card:active {
  transform: scale(0.98);
}

.feature-card.theme-past {
  background: linear-gradient(135deg, #e8f4ff 0%, #d6ebff 55%, #c5e0ff 100%);
}

.feature-card.theme-mock {
  background: linear-gradient(135deg, #fff4e6 0%, #ffe8cc 55%, #ffd9a8 100%);
}

.feature-card-text {
  flex: 1;
  min-width: 0;
  z-index: 1;
}

.feature-card-text h3 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
}

.feature-card-text p {
  margin: 0 0 8px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.35;
}

.feature-card-count {
  display: inline-block;
  font-size: 11px;
  color: #475569;
  background: rgba(255, 255, 255, 0.65);
  padding: 2px 8px;
  border-radius: 10px;
}

.feature-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 8px;
  align-self: center;
}

.theme-past .feature-card-icon {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.theme-mock .feature-card-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.tabs-wrap {
  background: #fff;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 10;
  margin-top: 4px;
}

.tabs-scroll {
  display: flex;
  overflow-x: auto;
  padding: 0 12px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.tabs-scroll::-webkit-scrollbar {
  display: none;
}

.tab-item {
  flex-shrink: 0;
  padding: 14px 16px;
  font-size: 15px;
  color: #666;
  position: relative;
  white-space: nowrap;
}

.tab-item.active {
  color: #1a1a2e;
  font-weight: 600;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  background: #1989fa;
  border-radius: 2px;
}

.list-section {
  padding: 16px;
}

.module-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.module-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  padding: 16px 14px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.module-card:active:not(.empty) {
  transform: scale(0.99);
}

.module-card.locked {
  opacity: 0.92;
}

.module-card.empty {
  opacity: 0.65;
}

.module-accent {
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 4px;
  background: linear-gradient(180deg, #1989fa, #096dd9);
  border-radius: 0 2px 2px 0;
}

.module-accent.accent-past {
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
}

.module-accent.accent-mock {
  background: linear-gradient(180deg, #f59e0b, #d97706);
}

.module-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 12px;
  min-width: 0;
}

.module-name {
  font-size: 16px;
  font-weight: 500;
  color: #1a1a2e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 12px;
}

.module-count {
  font-size: 13px;
  color: #999;
  flex-shrink: 0;
}

.module-lock,
.module-arrow {
  color: #c8c9cc;
  flex-shrink: 0;
  margin-left: 8px;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.bottom-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(56px + env(safe-area-inset-bottom));
  padding: 12px 24px 16px;
  background: linear-gradient(180deg, transparent, #f4f6f9 30%);
  pointer-events: none;
}

.trial-btn {
  pointer-events: auto;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  background: linear-gradient(90deg, #1989fa, #096dd9);
  border: none;
}
</style>
